<!-- @format -->
<template>
    <div class="sms-widget">
        <div class="widget-container">
            <div class="widget-header">
                <div class="header-text">
                    <l4-sms-svg />
                    <h3>Send SMS <app-beta-tag invert /></h3>
                </div>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="widget-content-wrapper">
                <div v-if="isLoading" class="loading-screen-container">
                    <loading-screen-svg />
                </div>
                <div v-else-if="currentUser">
                    <div>
                        <div class="contact-section-container">
                            <div class="contact-data">
                                <span>To: </span>
                                <from-search :contact="contact" />
                            </div>
                            <div class="contact-data">
                                <span>From: </span>
                                <contact-sms-select
                                    :initialId="currentUser.phoneNumberId"
                                    :options="availableUsers"
                                    @selectedUser="handleSelectedUser"
                                />
                            </div>
                        </div>
                        <div class="sms-content-wrapper">
                            <div v-if="isLoadingMessages" class="loading-screen-container">
                                <loading-screen-svg />
                            </div>
                            <contact-sms-message-history
                                id="message-history"
                                ref="messageHistory"
                                :last-message-id="lastMessageId"
                                :message-groups="messageGroups"
                                :page="currentPage"
                                :page-count="pageCount"
                                :has-loaded-all-older-messages="hasLoadedAllOlderMessages"
                                :user-profile-image="currentUser.imageUrl"
                                :contact-profile-image="contact.profileImageUrl"
                                @pull:top="handlePullTop"
                            />
                            <contact-sms-text-area
                                :clearTextArea="clearTextArea"
                                @textChange="handleTextChange"
                                @mediaChange="handleMediaChange"
                            />
                        </div>
                    </div>
                </div>
                <div v-else class="no-phone-numbers-wrapper">
                    <p>You do not have any phone numbers to send SMS from.</p>
                    <app-button @click="goToPhoneNumbers()"> Add a phone number </app-button>
                </div>
            </div>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button size="normal" @click="closeWidget"> Cancel</app-button>
                </div>
                <div class="action-container">
                    <app-button
                        :disabled="!(textData.length > 0 || mediaData.length > 0)"
                        @click="handleClickSendSMS"
                    >
                        Send SMS
                        <d5-send-svg />
                    </app-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import EventBus from '@/scripts/event-bus.js';
import { LogLevel } from "@/scripts/logger";

const ContactSmsTextArea = () =>
    import('@/components/contact/contact-sms/contact-sms-text-area.vue');
const AppButton = () => import('@/components/common/app-button.vue');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg.vue');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');
const ContactSmsSelect = () => import('@/components/contact/contact-sms/contact-sms-select.vue');
const FromSearch = () => import('@/components/contact/contact-sms/from-search.vue');
const ContactSmsMessageHistory = () =>
    import('@/components/contact/contact-sms/contact-sms-message-history');
const L4SmsSvg = () => import('@/components/svg/grid/l4-sms-svg');
const D5SendEmail = () => import('@/components/svg/grid/d5-sent-email-outline-svg');
const AppBetaTag = () => import('@/components/common/app-beta-tag');

export default {
    name: 'SmsWidget',

    components: {
        'app-button': AppButton,
        'contact-sms-message-history': ContactSmsMessageHistory,
        'contact-sms-select': ContactSmsSelect,
        'contact-sms-text-area': ContactSmsTextArea,
        'd5-send-svg': D5SendEmail,
        'from-search': FromSearch,
        'l4-sms-svg': L4SmsSvg,
        'loading-screen-svg': LoadingScreenSvg,
        'widget-close-svg': WidgetCloseSvg,
        'app-beta-tag': AppBetaTag,
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            availableUsers: [],
            clearTextArea: false,
            contact: {},
            currentPage: 1,
            currentUser: {},
            hasLoadedAllOlderMessages: false,
            isLoading: true,
            isLoadingMessages: true,
            mediaData: [],
            messages: [],
            messagesPerPage: 20,
            messagesRefreshInterval: null,
            messagesRefreshRateMs: 30000,
            textData: '',
            totalMessages: 0,
            usersSearchLimit: 50,
        };
    },

    computed: {
        clientId() {
            return this.$store.state.context.client.id;
        },

        routeBaseUrl() {
            return this.$store.getters.routeBaseUrl;
        },

        accountId() {
            return this.$store.state.context.account.id;
        },

        pageCount() {
            return Math.ceil(this.totalMessages / this.messagesPerPage);
        },

        lastMessageId() {
            return this.messages.length > 0 ? this.messages[this.messages.length - 1].id : null;
        },

        messageGroups() {
            const today = this.$moment();
            const yesterday = this.$moment().subtract(1, 'days');

            const keyByTime = (message) => {
                const currentDate = this.$moment(message.createdAt);
                if (currentDate.isSame(today, 'd')) {
                    return 'Today';
                } else if (currentDate.isSame(yesterday, 'd')) {
                    return 'Yesterday';
                } else {
                    return this.$moment(message.createdAt).format('MMMM Do YYYY');
                }
            };

            return this.$helpers.groupBy(this.messages, keyByTime);
        },
    },

    async created() {
        await this.getSendersData();

        if (this.currentUser) {
            await this.initializeMessages();
        } else {
        }
    },

    beforeDestroy() {
        if (this.messagesRefreshInterval) {
            clearInterval(this.messagesRefreshInterval);
        }
    },

    methods: {
        async initializeMessages() {
            const options = {
                page: 1,
                limit: this.messagesPerPage,
            };

            const { messages, count } = await this.getMessages(options);

            this.messages = messages;
            this.totalMessages = count;

            this.initMessagesHistoryInterval();
        },

        goToPhoneNumbers() {
            this.$router.push(`${this.routeBaseUrl}/Account/PhoneNumbers`);
            this.closeWidget();
        },

        asSmsItem(item) {
            const sourceIsContact =
                item.Participants.filter(
                    ({ SmsParticipantType, ContactId }) =>
                        SmsParticipantType === 'Sender' &&
                        ContactId === this.widget.options.contactId
                ).length !== 0;
            return {
                id: item.Id,
                body: item.Body,
                createdAt: item.CreatedAt,
                mediaUris: JSON.parse(item.MediaUris),
                source: sourceIsContact ? 'Contact' : 'User',
            };
        },

        initMessagesHistoryInterval() {
            this.messagesRefreshInterval = setInterval(async () => {
                // Interval always will take 20 message per page
                const { messages: newMessages, count } = await this.getMessages({ page: 1 });

                if (this.totalMessages < count) {
                    const newMessagesAmount = count - this.totalMessages;
                    const results = newMessages.reverse().slice(0, newMessagesAmount);
                    this.messages = [...this.messages, ...results];
                    this.totalMessages = count;
                }
            }, this.messagesRefreshRateMs);
        },

        async getMessages({ showLoading = false, limit = this.messagesPerPage, page }) {
            if (showLoading) {
                this.isLoadingMessages = true;
            }

            const { phoneNumberId } = this.currentUser;
            const { contactId } = this.widget.options;

            if (phoneNumberId && contactId) {
                try {
                    const service = '/service/Sms';

                    const filter = `$filter=Participants/any(p:p/PhoneNumberId eq ${phoneNumberId} and p/ContactId eq ${contactId})`;
                    const top = limit ? `$top=${limit}` : null;
                    const count = '$count=true';
                    const expand = '$expand=Participants';
                    const skip = page ? `$skip=${(page - 1) * limit}` : null;
                    const order = '$orderby=CreatedAt desc';
                    const queryParameters = [top, count, skip, order, expand, filter]
                        .filter(Boolean)
                        .join('&');
                    const restUrl = `${service}?${queryParameters}`;

                    const { data } = await this.$api.get(restUrl);

                    // Notice front-end is reversing messages.
                    const messages = data.value.reverse();
                    return {
                        messages: messages.map(this.asSmsItem),
                        count: data['@odata.count'],
                    };
                } catch (error) {
                    this.$helpers.showFailNotification(
                        'An error occurred while getting your messages.',
                        { error }
                    );
                    return { messages: this.messages, count: this.messages.length };
                } finally {
                    this.isLoadingMessages = false;
                }
            }
        },

        async getPhoneNumbers() {
            const phoneNumbersUrl = '/service/PhoneNumbers';
            const query = `?$filter=AccountId eq ${this.accountId}`;
            const { data } = await this.$api.get(`${phoneNumbersUrl}${query}`);
            return data.value;
        },

        async getUsers() {
            try {
                const phoneNumbers = await this.getPhoneNumbers();

                const top = `$top=${this.usersSearchLimit}`;
                const filter = `$filter=ClientId eq ${this.clientId}`;
                const sort = `$orderby=FirstName`;
                const queryParameters = `${filter}&${top}&$count=true&${sort}`;
                const restUrl = `/service/Users?${queryParameters}`;

                const { data } = await this.$api.get(restUrl);

                const users = data.value.reduce((acc, user) => {
                    const { Id, ProfileImageUrl, FirstName, LastName } = user;
                    const userPhoneNumbers = phoneNumbers.filter(({ UserId }) => UserId === Id);
                    const userData = userPhoneNumbers.map((phoneNumber) => ({
                        id: Id,
                        imageUrl: ProfileImageUrl || require('@/assets/images/avatar.png'),
                        mobilePhone: phoneNumber.Number,
                        phoneNumberId: phoneNumber.Id,
                        name: this.$helpers.getName(FirstName, LastName),
                    }));
                    acc.push(...userData);
                    return acc;
                }, []);

                this.availableUsers = users;

                if (this.widget.options.currentPhoneNumberId) {
                    const { currentPhoneNumberId } = this.widget.options;
                    this.currentUser = this.availableUsers.filter(
                        ({ phoneNumberId }) => phoneNumberId === +currentPhoneNumberId
                    )[0];
                    if (this.currentUser) {
                        return;
                    }
                    this.$helpers.showFailNotification('This conversation is not available.');
                }

                this.currentUser = this.availableUsers[0];
            } catch (error) {
                this.$helpers.showFailNotification(
                    'An error occurred while getting your available users.',
                    { error },
                );
                this.closeWidget();
            }
        },

        async getContact() {
            try {
                const { data } = await this.$api.get(
                    `/service/Contacts(${this.widget.options.contactId})`
                );

                const { Id, MobilePhone, FirstName, LastName, ProfileImageUrl } = data;

                if (MobilePhone === null || MobilePhone === '') {
                    this.$helpers.showFailNotification(
                        'Unable to send SMS to a contact without a mobile phone number.',
                        { level: LogLevel.INFO },
                    );
                    return this.closeWidget();
                }

                this.contact = {
                    id: Id,
                    mobilePhone: MobilePhone,
                    name: this.$helpers.getName(FirstName, LastName),
                    profileImageUrl: ProfileImageUrl || require('@/assets/images/avatar.png'),
                };
            } catch (error) {
                this.$helpers.showFailNotification(
                    'An error occurred while getting your contact information.',
                    { error },
                );
                this.closeWidget();
            }
        },

        async getSendersData() {
            this.isLoading = true;

            await this.getUsers();
            await this.getContact();

            this.isLoading = false;
        },

        async handleSelectedUser(selectedUser) {
            this.currentUser = selectedUser;
            this.currentPage = 1;
            this.messages = [];
            this.hasLoadedAllOlderMessages = false;

            const options = {
                limit: this.messagesPerPage,
                page: this.currentPage,
                showLoading: true,
            };

            const { messages, count } = await this.getMessages(options);
            this.messages = messages;
            this.totalMessages = count;
        },

        handleTextChange(data) {
            this.textData = data;
        },

        handleMediaChange(data) {
            this.mediaData = data;
        },

        async handleClickSendSMS() {
            const { phoneNumberId } = this.currentUser;
            const { contactId } = this.widget.options;

            if (phoneNumberId && contactId) {
                try {
                    const urls =
                        this.mediaData.length > 0 ? this.mediaData.map(({ url }) => url) : [];

                    const data = {
                        PhoneNumberId: phoneNumberId,
                        ContactId: contactId,
                        Body: this.textData,
                        MediaUris: urls,
                    };

                    await this.$api.post('/api/Sms', data);

                    const options = {
                        page: 1,
                    };

                    const { messages: newMessages, count } = await this.getMessages(options);

                    this.messages = this.mergeMessagesList(this.messages, newMessages);

                    this.totalMessages = count;

                    this.clearTextArea = !this.clearTextArea;
                } catch (error) {
                    this.$helpers.showFailNotification('Contact support.', { error });
                }
            }
        },

        mergeMessagesList(a1, a2) {
            const a1Serialized = [...a1].map((e) => JSON.stringify(e));
            const a2Serialized = [...a2].map((e) => JSON.stringify(e));
            return [...new Set([...a1Serialized, ...a2Serialized])].map((result) =>
                JSON.parse(result)
            );
        },

        async handlePullTop() {
            const backEndHasMessages = this.messages.length < this.totalMessages;

            if (
                backEndHasMessages &&
                !this.isLoadingMessages &&
                this.currentPage < this.pageCount
            ) {
                this.currentPage = this.currentPage += 1;

                const options = {
                    showLoading: true,
                    page: this.currentPage,
                };

                const { messages: oldestMessages } = await this.getMessages(options);

                // Prevents auto scrolling to the top
                this.$refs['messageHistory'].$el.scrollTo(0, 50);
                this.messages = this.mergeMessagesList(oldestMessages, this.messages);
            } else if (this.messages.length === this.totalMessages) {
                this.hasLoadedAllOlderMessages = true; // let the user know there's no older messages
            }
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },
    },
};
</script>

<style>
.sms-widget .widget-container {
    width: 100%;
    height: 100%;
}

.sms-widget .widget-header .header-text {
    align-items: center;
    display: flex;
}

.sms-widget .widget-header .header-text h3 {
    margin-left: 15px;
    display: flex;
    align-items: center;
}

.sms-widget .app-beta-tag {
    margin-left: 10px;
}

.sms-widget .widget-header .header-text svg {
    height: 30px;
    width: 35px;
}

.sms-widget .widget-header .header-text svg path {
    fill: #ffffff;
}

.sms-widget .widget-container .widget-content-wrapper {
    height: calc(100vh - 152px);
    padding: 0;
}

.sms-widget .loading-screen-container {
    height: 200px;
    overflow: hidden;
    text-align: center;
}

.sms-widget .loading-screen-container .loading-screen {
    height: 150px;
}

.sms-widget .widget-content-wrapper .contact-section-container {
    display: flex;
    flex-direction: row;
    height: 53px;
    justify-content: space-between;
    padding: 13px 21px 0 16px;
}

.sms-widget .contact-section-container .contact-data {
    align-items: center;
    display: flex;
    flex-direction: row;
    gap: 12px;
    max-width: 276px;
}

.sms-widget .contact-data .from-search,
.sms-widget .contact-data .contact-sms-select {
    color: #382153;
    font-weight: bold;
    width: 220px;
}

.sms-widget .sms-content-wrapper {
    display: flex;
    flex-direction: column;
    height: calc(100vh - 205px);
    padding: 12px 0 16px 0;
}

.sms-widget .widget-footer .cancel-container .app-button {
    background: transparent;
    border: 2px solid #cac6cb;
    color: #382153;
    width: 120px;
}

.sms-widget .widget-footer .cancel-container .app-button:hover {
    background: #e1e0e5;
}

.sms-widget .widget-footer .action-container svg {
    margin-left: 12px;
}

.sms-widget .widget-footer .action-container svg path {
    fill: #ffffff;
}

.sms-widget .no-phone-numbers-wrapper {
    padding: 45px 35px;
    text-align: center;
}

.sms-widget .no-phone-numbers-wrapper .app-button {
    margin: 0 auto;
}
</style>
